import type { Breadcrumb, ErrorEvent } from '@sentry/react';

/**
 * Keeps digital-receipt secrets out of Sentry and PostHog.
 *
 * A receipt link is `https://<school>/r#<secret>`: a permanent bearer link to
 * a payment. The fragment never reaches our servers, but the browser SDKs see
 * `location.href` and anything the page logs, so:
 *
 * - On `/r`, `/claim` and `/password-reset` PostHog is never mounted (see `analytics.tsx`), console
 *   breadcrumbs are dropped, and every event is deep-scrubbed of `token` keys
 *   and of the page's own secret wherever it appears.
 * - Everywhere, any `/r#<secret>` fragment in an event is replaced — the staff
 *   success step holds the link in memory, and a stray log there must not
 *   carry it either.
 *
 * Nothing else is touched: an event with no receipt link, sent from any page
 * other than `/r`, comes back as the very same object.
 */

const FILTERED = '[Filtered]';
const SECRET_FRAGMENT = /(\/(?:r|claim|password-reset)\/?)#[A-Za-z0-9_-]+/g;
const SENSITIVE_KEYS = new Set(['token', 'secret']);
const MAX_DEPTH = 20;
// token_urlsafe(16) is 22 chars; anything much shorter isn't one of ours and
// replacing it everywhere would mangle unrelated text.
const MIN_SECRET_LENGTH = 16;

export const isReceiptPath = (pathname: string): boolean => /^\/r\/?$/.test(pathname);

/** Public pages whose URL fragment is a bearer secret: the receipt (`/r#…`), the invite claim (`/claim#…`) and the password reset (`/password-reset#…`). */
export const isSecretFragmentPath = (pathname: string): boolean => /^\/(?:r|claim|password-reset)\/?$/.test(pathname);

const onReceiptPage = () =>
  typeof window !== 'undefined' && isSecretFragmentPath(window.location.pathname);

const currentSecret = (): string | null => {
  const secret = window.location.hash.replace(/^#/, '');
  return secret.length >= MIN_SECRET_LENGTH ? secret : null;
};

type ScrubOptions = {
  /** Also filter `token`/`secret` keys and the page's own secret (only on /r). */
  strict: boolean;
  secret: string | null;
};

const scrubString = (value: string, { secret }: ScrubOptions): string => {
  let next = value.replace(SECRET_FRAGMENT, `$1#${FILTERED}`);
  if (secret && next.includes(secret)) next = next.split(secret).join(FILTERED);
  return next;
};

/**
 * Returns `value` itself when nothing needed scrubbing, and a scrubbed copy of
 * only the branches that changed otherwise — so pass-through stays free and
 * identity-preserving.
 */
const scrub = (value: unknown, options: ScrubOptions, depth = 0, seen = new WeakSet<object>()): unknown => {
  if (typeof value === 'string') return scrubString(value, options);
  if (value === null || typeof value !== 'object' || depth > MAX_DEPTH) return value;
  if (seen.has(value)) return value;
  seen.add(value);

  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map(item => {
      const scrubbed = scrub(item, options, depth + 1, seen);
      if (scrubbed !== item) changed = true;
      return scrubbed;
    });
    return changed ? next : value;
  }

  // Only plain objects are walked; class instances (Errors, Dates) are left as
  // they are rather than flattened into something Sentry can't serialise.
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;

  let changed = false;
  const next: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    const scrubbed =
      options.strict && SENSITIVE_KEYS.has(key.toLowerCase()) && item != null
        ? FILTERED
        : scrub(item, options, depth + 1, seen);
    if (scrubbed !== item) changed = true;
    next[key] = scrubbed;
  }
  return changed ? next : value;
};

const scrubOptions = (): ScrubOptions => {
  const strict = onReceiptPage();
  return { strict, secret: strict ? currentSecret() : null };
};

export const receiptBeforeSend = <T extends ErrorEvent>(event: T): T =>
  scrub(event, scrubOptions()) as T;

export const receiptBeforeBreadcrumb = <T extends Breadcrumb>(breadcrumb: T): T | null => {
  const options = scrubOptions();
  // Console output on /r is never worth the risk: an error logged with its
  // request args would carry the token in the body.
  if (options.strict && breadcrumb.category === 'console') return null;
  return scrub(breadcrumb, options) as T;
};
